import createClient, { type Middleware } from 'openapi-fetch';
import type { components as HoComponents, paths as HoPaths } from './generated/ho';
import type { paths as OnlineTablePaths } from './generated/online-table';
import type { paths as OnlineWebPaths } from './generated/online-web';
import type { components, paths } from './generated/store';

export type { components, paths } from './generated/store';
export type { components as HoComponents, paths as HoPaths } from './generated/ho';
// Online plane (ADR 0053): the website's and table QR ordering's APIs.
export type { components as OnlineWebComponents, paths as OnlineWebPaths } from './generated/online-web';
export type { components as OnlineTableComponents, paths as OnlineTablePaths } from './generated/online-table';
export type BusinessDayDto = components['schemas']['BusinessDayDto'];
export type CurrentShiftDto = components['schemas']['CurrentShiftDto'];
export type ShiftDto = components['schemas']['ShiftDto'];
export type ShiftSummaryDto = components['schemas']['ShiftSummaryDto'];
export type AttendanceDto = components['schemas']['AttendanceDto'];
export type AttendancePage = components['schemas']['AttendancePage'];
export type AttendancePunchDto = components['schemas']['AttendancePunchDto'];
export type CashLine = components['schemas']['CashLine'];
// The day report (FR-SHF-06, FR-RPT-02).
export type DayReportDto = components['schemas']['DayReportDto'];
export type DaySalesGroup = components['schemas']['DaySalesGroup'];
export type SessionDto = components['schemas']['SessionDto'];
export type StaffDto = components['schemas']['StaffDto'];
export type ApprovalDto = components['schemas']['ApprovalDto'];
// "PDF on terminal" printers (ADR 0046).
export type TerminalPdfJob = components['schemas']['TerminalPdfJobDto'];
export type BrandDto = HoComponents['schemas']['BrandDto'];
export type BrandInput = HoComponents['schemas']['BrandInput'];
// Store ↔ HO connection (ADR 0014).
export type SyncStatusDto = components['schemas']['SyncStatusDto'];
export type StoreConnectionDto = HoComponents['schemas']['StoreConnectionDto'];
export type PairingCodeDto = HoComponents['schemas']['PairingCodeDto'];
// Overview endpoints (the store and HO hosts return the same sales shapes; the fleet is HO only).
export type SalesSummaryResponse = HoComponents['schemas']['SalesSummaryDto'];
export type SalesSeriesResponse = HoComponents['schemas']['SalesSeriesDto'];
export type FleetOverviewResponse = HoComponents['schemas']['FleetOverviewDto'];

/** RFC 9457 problem returned by the backend; `code` is the stable business error code. */
export interface ApiProblem {
  status?: number;
  title?: string;
  code?: string;
  /** Validation errors by property name, from FluentValidation. */
  errors?: Record<string, string[]>;
  [extension: string]: unknown;
}

export interface ApiOptions {
  /** Empty in the apps: they are served by (or proxied to) their backend, so calls are same-origin. */
  baseUrl?: string;
  getAccessToken(): string | undefined;
  /** Called when a request made with a token gets 401: the session ended (expired, user locked). */
  onUnauthorized?(): void;
  /** The UI language; sent as Accept-Language so backend messages match the screen. */
  getLanguage?(): string;
  /** Replaces the global fetch, e.g. to notice when the store server can't be reached. */
  fetch?: typeof globalThis.fetch;
}

/** Staff login (FR-AUTH-01), also from the idle lock (FR-AUTH-05). */
const LOGIN_PATH = '/api/v1/identity/sessions';

function authMiddleware({ getAccessToken, onUnauthorized, getLanguage }: ApiOptions): Middleware {
  return {
    onRequest({ request, schemaPath }) {
      const token = getAccessToken();
      // Logging in is how a token is got: a locked terminal's old one must not go with it, or a wrong password (401) would
      // read as the session ending and log the locked-out staff member off instead of saying the password is wrong.
      const login = request.method === 'POST' && schemaPath === LOGIN_PATH;
      // A call made with a token of its own keeps it: the POS's terminal setup lists terminals and logs off with the manager's
      // session while a cashier may still be signed in on the device.
      if (token && !login && !request.headers.has('Authorization')) request.headers.set('Authorization', `Bearer ${token}`);
      const language = getLanguage?.();
      if (language) request.headers.set('Accept-Language', language);
    },
    // Both return nothing: the headers are set in place and the response is unchanged. Returning them would fail
    // openapi-fetch's `instanceof` checks in React Native, where Expo's fetch has its own Response class (ADR 0105).
    onResponse({ request, response }) {
      // Only the session's own token ending ends the session, not another one a call brought.
      const token = getAccessToken();
      if (response.status === 401 && token && request.headers.get('Authorization') === `Bearer ${token}`) onUnauthorized?.();
    },
  };
}

/** The store backend: POS, KDS and the store admin portal. The only way frontends call it. */
export function createStoreApi(options: ApiOptions) {
  const client = createClient<paths>({ baseUrl: options.baseUrl ?? '', ...(options.fetch ? { fetch: options.fetch } : {}) });
  client.use(authMiddleware(options));
  return client;
}

/** The HO admin API: the HO admin portal. */
export function createHoApi(options: ApiOptions) {
  const client = createClient<HoPaths>({ baseUrl: options.baseUrl ?? '' });
  client.use(authMiddleware(options));
  return client;
}

/** The website's API (online plane, ADR 0053): same-origin under /api on each brand domain. No staff tokens. */
export function createOnlineWebApi(options: Omit<ApiOptions, 'getAccessToken'> & Partial<Pick<ApiOptions, 'getAccessToken'>>) {
  const client = createClient<OnlineWebPaths>({ baseUrl: options.baseUrl ?? '', ...(options.fetch ? { fetch: options.fetch } : {}) });
  client.use(authMiddleware({ getAccessToken: () => undefined, ...options }));
  return client;
}

/** Table QR ordering's API (online plane, ADR 0053): no login; the table comes from its QR token. */
export function createOnlineTableApi(options: Pick<ApiOptions, 'baseUrl' | 'getLanguage' | 'fetch'>) {
  const client = createClient<OnlineTablePaths>({ baseUrl: options.baseUrl ?? '', ...(options.fetch ? { fetch: options.fetch } : {}) });
  client.use(authMiddleware({ getAccessToken: () => undefined, ...options }));
  return client;
}

export type StoreApi = ReturnType<typeof createStoreApi>;
export type HoApi = ReturnType<typeof createHoApi>;

export * from './orders';
export * from './locations';
